import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  // Initialize state from localStorage synchronously
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem('token') || null;
    } catch {
      return null;
    }
  });
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Memoized axios instance
  const axiosAuth = useCallback(() => {
    const currentToken = token || localStorage.getItem('token');
    return axios.create({
      baseURL: API,
      headers: {
        Authorization: currentToken ? `Bearer ${currentToken}` : '',
        'Content-Type': 'application/json'
      }
    });
  }, [token]);

  // Verify token on mount
  useEffect(() => {
    let cancelled = false;
    
    const verifyToken = async () => {
      const storedToken = localStorage.getItem('token');
      
      if (!storedToken) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` },
          timeout: 15000
        });
        
        if (!cancelled) {
          setUser(response.data);
          setToken(storedToken);
        }
      } catch (error) {
        console.error('Token verification failed:', error);
        if (!cancelled) {
          // Clear invalid token
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    verifyToken();
    
    return () => {
      cancelled = true;
    };
  }, []); // Run once on mount

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    return response.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  }, []);

  const completeOnboarding = useCallback(async () => {
    const currentToken = token || localStorage.getItem('token');
    await axios.post(`${API}/auth/complete-onboarding`, {}, {
      headers: { Authorization: `Bearer ${currentToken}` }
    });
    setUser(prev => ({ ...prev, onboarding_completed: true }));
  }, [token]);

  // Memoize context value
  const contextValue = useMemo(() => ({
    user,
    token,
    loading,
    login,
    register,
    logout,
    axiosAuth,
    completeOnboarding
  }), [user, token, loading, login, register, logout, axiosAuth, completeOnboarding]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
